import type { SvgIconComponent } from '@mui/icons-material';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { Box, Tooltip } from '@mui/material';

import type { ActionItemSeverity } from '~/data/schemas/actionItem';
import { visuallyHidden } from '~/utils/visuallyHidden';

export const SEVERITY_LABEL: Record<ActionItemSeverity, string> = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info',
};

/** Highest first — the order the band reads in and the grid sorts by. */
export const SEVERITY_ORDER: ActionItemSeverity[] = ['critical', 'warning', 'info'];

/** Rank for sorting: a bigger number is more urgent. */
export const severityRank = (severity: ActionItemSeverity): number =>
  SEVERITY_ORDER.length - SEVERITY_ORDER.indexOf(severity);

const SEVERITY_COLOR: Record<ActionItemSeverity, string> = {
  critical: 'error.main',
  warning: 'warning.main',
  info: 'info.main',
};

/**
 * A distinct silhouette per level, not a coloured dot. The three dots the queue used to
 * draw were the same shape at the same size, so the column carried its whole meaning in
 * hue — unreadable to a colourblind reader and gone in a greyscale print.
 */
const SEVERITY_ICON: Record<ActionItemSeverity, SvgIconComponent> = {
  critical: ErrorRoundedIcon,
  warning: WarningRoundedIcon,
  info: InfoRoundedIcon,
};

/** The severity cell: one icon, its name on hover, and its name for a reader. */
export function SeverityIcon({ severity }: { severity: ActionItemSeverity }) {
  const Icon = SEVERITY_ICON[severity] ?? InfoRoundedIcon;
  const label = SEVERITY_LABEL[severity] ?? severity;

  return (
    <Tooltip title={label}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="span" sx={{ ...visuallyHidden }}>
          {label}
        </Box>
        <Icon
          aria-hidden
          sx={{ fontSize: 20, color: SEVERITY_COLOR[severity] ?? 'text.disabled' }}
        />
      </Box>
    </Tooltip>
  );
}
